/**
 * Pure utility functions for prescription label data — mapping orders and
 * treatments to the label shape, filtering printable orders, and one-off
 * PDF preview generation.
 */
import { createElement } from 'react'

import type {
  Clinic,
  DateFormat,
  Order,
  OrderWithRelations,
  RegionId,
  StaffMember,
  TreatmentWithRelations,
} from '@snootsvet/shared'
import {
  formatCalendarDate,
  formatInstantDate,
  formatName,
  getStaffRegistrationLabel,
  resolveDateFormat,
} from '@snootsvet/shared'

import {
  MultiPrescriptionLabel,
  type PrescriptionLabelData,
} from '@/frontend/features/printing/labels/prescription-label.print'
import { previewPdf } from '@/frontend/features/printing/pdf-utils'

export type { PrescriptionLabelData }

/**
 * Build a display-ready prescriber registration line using the region-appropriate
 * label (e.g. "RCVS number 1234567" in the UK, "NJ license number ..." in the US).
 * Returns null when the vet has no license number on file.
 */
export function formatStaffRegistration(
  regions: RegionId[],
  licenseNumber: string | null
): string | null {
  if (!licenseNumber) return null
  return `${getStaffRegistrationLabel(regions)} ${licenseNumber}`
}

/**
 * Generate a multi-page prescription label PDF and open it in a new tab.
 * Standalone utility for one-off prints outside the hook lifecycle.
 */
export async function previewPdfLabels(labels: PrescriptionLabelData[]): Promise<void> {
  if (labels.length === 0) return
  const filename = `labels-${labels[0].patient.name.toLowerCase().replace(/\s+/g, '-')}.pdf`
  const element = createElement(MultiPrescriptionLabel, { labels })
  await previewPdf(element, filename)
}

/**
 * Whether an order should get a prescription label — only take-home dispensations
 * need one.
 *
 * Items administered in consult are excluded by the status check alone, so don't
 * add an `administeredInConsult` guard here: their order is created as
 * 'administered' and stays that way permanently (see `treatmentIsCommitted`), so
 * it never reaches 'prepared' or 'collected'. Checking the treatment-level flag
 * as well would be dead code at best, and at worst would suppress the label on a
 * refill order that genuinely goes home.
 */
export function shouldPrintLabel(order: Pick<Order, 'status'>): boolean {
  return order.status === 'prepared' || order.status === 'collected'
}

/**
 * Fully-resolved fields for a single prescription label, before final assembly.
 * The domain adapters (`orderToLabelData`, `treatmentToLabelData`,
 * `committedTreatmentToLabelData`) each resolve their source object down to this
 * shape; `buildLabelData` owns the actual label structure and shared formatting.
 */
interface LabelDataInput {
  clinic: Pick<Clinic, 'name' | 'address'> | null
  patient: { name: string; species: string; breed: string | null }
  ownerName: string
  /** Pre-formatted latest recorded weight, or null when none is on file. */
  weight: string | null
  /** Prescribing vet — the label formats their name and registration line. */
  prescriber: StaffMember | null
  /** ISO timestamp; formatted for display by the assembler. */
  date: string
  /**
   * How to render the label's dates. A label is generated output that goes home
   * with the owner, so it follows the dispensing clinic's zone and the owner's
   * region — never the printing staff member's.
   */
  fmt: DateFormat
  medication: string
  directions: string | null
  /** Display-ready quantity string, e.g. "3 tablets". */
  quantity: string
  repeatsAllowed: number | null
  /** Batches to print; expiry dates already display-formatted. */
  batches: { batchNumber: string; expiryDate: string | null }[]
}

/**
 * Single source of truth for the prescription label shape. Owns the clinic
 * fallbacks, prescriber name/registration formatting, and date formatting that
 * every label needs. Callers hand it fully-resolved fields via `LabelDataInput`.
 */
function buildLabelData(input: LabelDataInput): PrescriptionLabelData {
  const { clinic, patient, ownerName, weight, prescriber } = input

  return {
    clinic: {
      name: clinic?.name ?? '—',
      address: clinic?.address ?? '',
    },
    patient: {
      name: patient.name,
      species: patient.species,
      breed: patient.breed,
      ownerName,
      weight,
    },
    prescription: {
      date: formatInstantDate(input.date, input.fmt),
      medication: input.medication,
      directions: input.directions,
      quantity: input.quantity,
      repeatsAllowed: input.repeatsAllowed,
      batches: input.batches,
    },
    prescriber: {
      name: prescriber ? formatName(prescriber) : '—',
      qualification: null, // TODO: add when StaffMember includes qualification
      licenseNumber: prescriber
        ? formatStaffRegistration(prescriber.regions, prescriber.licenseNumber)
        : null,
    },
  }
}

/**
 * Map a committed order (with nested relations) to prescription label data.
 * Batch allocations come directly from the order — no speculative fetch needed.
 *
 * `weight` is the pre-formatted latest recorded weight (from the patient's
 * vitals), or null when no weight is on file. Callers resolve it via
 * `formatLatestWeight` since the adapter has no access to region context.
 */
export function orderToLabelData(
  order: OrderWithRelations,
  weight: string | null = null
): PrescriptionLabelData {
  // `orders.clinicId` is NOT NULL, so the dispensing clinic always resolves and
  // decides the format outright — the owner's region is passed only to satisfy the
  // clinic-less branch, which an order can never take.
  const fmt = resolveDateFormat(order.clinic.id, order.patient.owner.region)

  return buildLabelData({
    clinic: order.clinic,
    patient: order.patient,
    ownerName: formatName(order.patient.owner),
    weight,
    prescriber: order.treatment?.staff ?? null,
    date: order.createdAt,
    fmt,
    medication: order.product.name,
    directions: order.treatment?.instructions ?? null,
    quantity: `${order.quantity} ${order.product.unit}`,
    repeatsAllowed: order.treatment?.repeatsAllowed ?? null,
    batches: order.allocations
      .filter((a) => !a.reversedAt && a.stock.batchNumber)
      .map((a) => ({
        batchNumber: a.stock.batchNumber!,
        // `stock.expiryDate` is a `date` column — it names a day, so no zone applies.
        expiryDate: a.stock.expiryDate ? formatCalendarDate(a.stock.expiryDate, fmt) : null,
      })),
  })
}

export interface TreatmentLabelContext {
  treatment: {
    product: { name: string; unit: string }
    instructions: string | null
    quantity: number
    repeatsAllowed: number | null
  }
  clinic: Clinic | undefined
  patient: { name: string; species: string; breed: string | null }
  ownerName: string
  /** Pre-formatted latest recorded weight, or null when none is on file. */
  weight: string | null
  /** Prescribing vet, or null. */
  prescriber: StaffMember | null
  batches: { batchNumber: string; expiryDate: string | null }[]
  /** Resolved from the dispensing clinic and the owner's region — see `LabelDataInput`. */
  fmt: DateFormat
}

/**
 * Map an in-progress consultation treatment to label data. Unlike
 * `orderToLabelData`, batch info must be supplied externally (computed via FEFO
 * allocation since no order exists yet) and the date is "now" — the prescription
 * is being written as it's printed.
 */
export function treatmentToLabelData(ctx: TreatmentLabelContext): PrescriptionLabelData {
  const { treatment: t, clinic, patient, ownerName, weight, prescriber, batches, fmt } = ctx

  return buildLabelData({
    clinic: clinic ?? null,
    patient,
    ownerName,
    weight,
    prescriber,
    date: new Date().toISOString(),
    fmt,
    medication: t.product.name,
    directions: t.instructions,
    quantity: `${t.quantity} ${t.product.unit}`,
    repeatsAllowed: t.repeatsAllowed,
    batches,
  })
}

export interface CommittedTreatmentLabelContext {
  /** The prescription treatment, with its committed order(s) and allocations. */
  treatment: TreatmentWithRelations
  patient: { name: string; species: string; breed: string | null }
  ownerName: string
  clinic: Pick<Clinic, 'name' | 'address'> | null
  /** Pre-formatted latest recorded weight, or null when none is on file. */
  weight: string | null
  /** Resolved from the dispensing clinic and the owner's region — see `LabelDataInput`. */
  fmt: DateFormat
}

/**
 * Map a committed consultation treatment to prescription label data. Used when
 * (re)printing a dispensing label from a read-only consultation card — batch
 * allocations come from the treatment's already-prepared order(s), and the
 * patient/clinic context is supplied by the enclosing consultation.
 */
export function committedTreatmentToLabelData(
  ctx: CommittedTreatmentLabelContext
): PrescriptionLabelData {
  const { treatment: t, patient, ownerName, clinic, weight, fmt } = ctx

  // Collect batches across the treatment's order(s), skipping reversed allocations
  // and de-duplicating batches that repeat across refill orders. Every non-reversed
  // allocation counts — an order that's `prepared` but not yet `collected` is still
  // allocated to its batch, which is the batch that will physically go home. We
  // don't filter by order status, so a treatment with multiple live orders lists
  // all their batches.
  const seen = new Set<string>()
  const batches: { batchNumber: string; expiryDate: string | null }[] = []
  for (const order of t.orders) {
    for (const allocation of order.allocations) {
      if (allocation.reversedAt || !allocation.stock.batchNumber) continue
      const key = `${allocation.stock.batchNumber}|${allocation.stock.expiryDate}`
      if (seen.has(key)) continue
      seen.add(key)
      batches.push({
        batchNumber: allocation.stock.batchNumber,
        // `stock.expiryDate` is a `date` column — it names a day, so no zone applies.
        expiryDate: allocation.stock.expiryDate
          ? formatCalendarDate(allocation.stock.expiryDate, fmt)
          : null,
      })
    }
  }

  return buildLabelData({
    clinic,
    patient,
    ownerName,
    weight,
    prescriber: t.staff,
    date: t.createdAt,
    fmt,
    medication: t.productInfo.name,
    directions: t.instructions,
    quantity: t.productInfo.quantityLabel,
    repeatsAllowed: t.repeatsAllowed,
    batches,
  })
}
